import { LocationNode } from "../models/locations";
import { AssetsPaginatedResponse, ProjectId } from "../types";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

const DEFAULT_TREE_ID = "default";

export interface GetLocationNodesOptions {
  /** Defaults to `"default"` — the ACC Locations Service has no endpoint to list a
   * project's trees, and every project has exactly one tree flagged as its default. */
  treeId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Creates a Locations-service client from an already-configured `AssetsHttpClient`
 * instance pointed at the Locations backend origin — a **different origin** than the
 * Assets service (see the "second-origin" design note in `createAssetsHttpClient.ts`,
 * also used by `hqClient`/`relationshipClient`).
 *
 * Read-only by design: the ACC Locations Service's write endpoints are out of scope —
 * this client never creates, updates, or deletes a Location.
 *
 * Caution: the ACC Locations v2 API this targets does **not** work with BIM360 projects
 * — the service returns an error for a non-ACC-unified project.
 *
 * @example
 * ```ts
 * const locationsHttp = createAssetsHttpClient({
 *   fetch: (url, init) => fetchWithToken(url, { ...init, headers: { region: getRegion(), ...init?.headers } }),
 *   baseUrl: getEnvValue("LOCATIONS_BACKEND"),
 * });
 * const client = getLocationClient(locationsHttp, projectId);
 * const allNodes = await client.listAllNodes();
 * ```
 */
export const getLocationClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => {
  const getLocationNodes = ({
    treeId = DEFAULT_TREE_ID,
    limit,
    offset,
  }: GetLocationNodesOptions = {}): Promise<
    AssetsPaginatedResponse<LocationNode[]>
  > => {
    const searchString = buildSearchString({ limit, offset });
    return http.get<AssetsPaginatedResponse<LocationNode[]>>(
      `${http.baseUrl}/v2/projects/${projectId}/trees/${treeId}/nodes${
        searchString ? `?${searchString}` : ""
      }`
    );
  };

  const listAllNodes = async ({
    treeId = DEFAULT_TREE_ID,
  }: Pick<GetLocationNodesOptions, "treeId"> = {}): Promise<LocationNode[]> => {
    const nodes: LocationNode[] = [];
    let nextUrl: string | undefined;

    do {
      const response: AssetsPaginatedResponse<LocationNode[]> = nextUrl
        ? await http.get<AssetsPaginatedResponse<LocationNode[]>>(nextUrl)
        : await getLocationNodes({ treeId });
      nodes.push(...response.results);
      nextUrl = response.pagination.nextUrl;
    } while (nextUrl);

    return nodes;
  };

  return { getLocationNodes, listAllNodes };
};

/**
 * The shape returned by {@link getLocationClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getLocationClient>`.
 */
export type LocationClient = ReturnType<typeof getLocationClient>;
